
import { computed, defineComponent, onMounted, PropType, reactive, ref, Ref } from 'vue'

import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'
import { useTablesStore } from '@/modules/TablesModule/store/useTablesStore'
import { ColumnItemT } from '@/core/types/tables.types'
import { useRoute } from 'vue-router'
import NewModal from '@/common/new-components/NewModal/index.vue'

import draggable from 'vuedraggable'

export default defineComponent({
    components: { NewCheckbox, NewModal, draggable },
    props: {
        rows: {
            type: Array as PropType<any[]>,
            default: () => [],
        },
        clickableRows: {
            type: Boolean,
            default: false,
        },
        columns: {
            type: Array as PropType<any[]>,
            default: () => [],
        },
        small: {
            type: Boolean,
            default: false,
        },
        selectIndexes: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        selectClass: {
            type: String,
            default: 'bg-blue-100',
        },
        paddingBottom: {
            type: String,
            default: null,
        },
        fixedCells: {
            type: Boolean,
            default: false,
        },
        hasCheckboxColumn: {
            type: Boolean,
            default: false,
        },
        hasIndexColumn: {
            type: Boolean,
            default: false,
        },
        selectedRows: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        nestedRows: {
            type: Array as PropType<any[]>,
            default: () => [],
        },
        nestedRowsExpandKey: {
            type: String,
            default: null,
        },
        expandedRows: {
            type: Array as PropType<number[]>,
            default: () => [],
        },
        fixed: {
            type: Boolean,
            default: false,
        },
        fixedHeight: {
            type: Boolean,
            default: true,
        },
        selectRowBy: {
            type: String,
            default: 'id',
        },
    },
    emits: [
        'row-click',
        'select-row',
        'unselect-row',
        'select-all',
        'unselect-all',
        'parent-row-click',
        'filter',
    ],
    setup(props, { emit }) {
        const { path } = useRoute()
        const tablesStore = useTablesStore()

        const state = reactive({
            is_all_checked: false,
            columns: [] as ColumnItemT[],
            fake_width: null as number | null,
            drag: false,
        })

        const onRowClick = (row: any) => {
            if (!props.clickableRows) return
            emit('row-click', { ...row })
        }

        const onParentRowClick = (parent_row: any) => {
            if (tbody.value) {
                // Add top-scroll when table fethced some data
                setTimeout(() => {
                    if (tbody.value) state.fake_width = tbody.value.offsetWidth
                }, 100)
            }

            emit('parent-row-click', { ...parent_row })
        }

        const isRowSelected = (row: any) => {
            return props.selectedRows.includes(row[props.selectRowBy])
        }

        const onSelectRow = (row: any) => {
            if (isRowSelected(row)) {
                emit('unselect-row', { ...row })
            } else {
                emit('select-row', { ...row })
            }
        }

        const onSelectAll = (value: boolean) => {
            if (value) emit('select-all')
            else emit('unselect-all')
        }

        const isNestedRowExpanded = (nestedRow: any) => {
            return props.expandedRows.includes(nestedRow.id)
        }

        const onFilterClick = (key: string) => {
            emit('filter', key)
        }

        const getColumns = () => (state.columns = tablesStore.getColumns(path, props.columns))
        const setColumns = () => tablesStore.setColumns(path, state.columns)

        const filteredColumns = computed(() => state.columns.filter((c) => c.isChecked))

        const onAllColumnsSelect = (value: boolean) => {
            state.columns.forEach((column) => {
                column.isChecked = value
            })
        }

        const isAllColumnsChecked = computed(() =>
            state.columns.every((column) => column.isChecked)
        )

        const thead: Ref<HTMLElement | null> = ref(null)
        const table: Ref<HTMLElement | null> = ref(null)
        const tbody: Ref<HTMLElement | null> = ref(null)

        const onScrollTbody = (e: any) => {
            if (!props.fixed) return
            if (thead.value) thead.value.scrollLeft = e.target.scrollLeft
        }

        const onScrollThead = (e: any) => {
            if (table.value) table.value.scrollLeft = e.target.scrollLeft
        }

        const getTableHolderClass = computed(() => {
            let classStr = ''

            if (props.fixed) classStr += 'fixed '
            if (props.fixedHeight) classStr += ' NewTableHolder--fit-height'
            if (props.fixedCells) classStr += ' NewTableHolder--fixed-cells'

            return classStr
        })

        const onDragEnd = () => {
            state.drag = false
        }

        onMounted(getColumns)

        return {
            tablesStore,
            state,
            //
            isNestedRowExpanded,
            onRowClick,
            getTableHolderClass,
            onParentRowClick,
            isRowSelected,
            onSelectRow,
            onSelectAll,
            onFilterClick,
            setColumns,
            filteredColumns,
            onAllColumnsSelect,
            isAllColumnsChecked,
            onScrollTbody,
            onScrollThead,
            thead,
            table,
            tbody,
            onDragEnd,
        }
    },
})
